import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useContext } from 'react';
import _ from 'underscore';
import { useFetch } from 'use-http';
import classNames from 'classnames';
import { Stack, Spinner, SpinnerSize, MessageBar, MessageBarType } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { getReachEstimateStyles } from './reach-estimate-styles';
import { AudienceMeter } from './audience-meter';
import { unitValueMap } from './utils';

export const tooNarrowFlag = 'ToNarrow';
export const tooNarrowErrorMessage = _TL_('Your targeting is too narrow. Update your targets to broaden your audience and increase estimated impressions.');

const title = _TL_('Potential monthly impressions');

const ReachEstimate = (props) => {
  const {
    i18n,
    permissions,
    targets,
    accountId,
    customerId,
    updateValidation,
    validationProps,
    shouldServeOnMSAN,
  } = props;

  const classes = useDefaultStyles(getReachEstimateStyles);

  const activity = useContext(ActivityContext);

  const {
    post: fetchReachEstimation,
    response: reachEstimationResponse,
    loading: reachEstimationLoading,
    error: reachEstimationError,
  } = useFetch(`Customers(${customerId})/Accounts(${accountId})/planner/AudienceEstimation`);

  const [currentTargets, setCurrentTargets] = useState(null);

  const [showPanel, setShowPanel] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const [estimatedImpressions, setEstimatedImpressions] = useState();
  const [estimatedImpressionsText, setEstimatedImpressionsText] = useState();
  const [isImpressionTooSpecific, setIsImpressionTooSpecific] = useState();

  const [errorMessage, setErrorMessage] = useState();

  const updateEstimatesOnMeter = useCallback((data) => {
    if (!data || !data.estImpression) {
      return;
    }

    const estImpressionsVal = data.estImpression.value;
    const estImpressionsUnit = unitValueMap[data.estImpression.unit].unit;
    const estImpressionsUnitVal = unitValueMap[data.estImpression.unit].value;

    const estImpressionsText = `${estImpressionsVal}${estImpressionsUnit}`;
    const estImpressions = estImpressionsVal * estImpressionsUnitVal;

    setEstimatedImpressions(estImpressions);
    setEstimatedImpressionsText(estImpressionsText);
    setIsImpressionTooSpecific(data.isImpressionTooSpecific);
  }, []);

  useEffect(() => {
    if (shouldServeOnMSAN) {
      if (isImpressionTooSpecific && !permissions.IsAmplifyEnabled) {
        updateValidation([i18n.getString(_TL_('Targeting too specific'))], { [validationProps.field]: tooNarrowFlag }, false);
      } else if (isLoading) {
        updateValidation([i18n.getString(_TL_('Loading'))], { [validationProps.field]: null }, false);
      } else {
        updateValidation([], { [validationProps.field]: null }, false);
      }
    }
  }, [i18n, isLoading, updateValidation, isImpressionTooSpecific, permissions.IsAmplifyEnabled, validationProps, shouldServeOnMSAN]);

  const updatePanel = useCallback((data) => {
    let currentErrorMessage = null;
    if (_.get(data, ['estImpression', 'value']) || data.isImpressionTooSpecific) {
      setShowPanel(true);
      updateEstimatesOnMeter(data);

      if (data.isImpressionTooSpecific && !permissions.IsAmplifyEnabled) {
        currentErrorMessage = tooNarrowErrorMessage;
      }
      setErrorMessage(currentErrorMessage && i18n.getString(currentErrorMessage));
    } else {
      setShowPanel(false);
    }
  }, [i18n, permissions.IsAmplifyEnabled, updateEstimatesOnMeter]);

  useEffect(() => {
    if (!reachEstimationLoading) {
      const { data } = reachEstimationResponse;

      setIsLoading(false);

      if (data) {
        updatePanel(data);
      }

      if (reachEstimationError) {
        setShowPanel(false);
      }

      if (activity) {
        activity.signal(`onReachEstimationChange - ${JSON.stringify({ estimation: data })}`);
      }
    }
  }, [reachEstimationError, reachEstimationLoading, reachEstimationResponse, updateValidation, updatePanel, activity]);

  // Allow Updating Estimates no more than once every 2000 ms
  const debouncedUpdateEstimate = useCallback(_.debounce(data => fetchReachEstimation('', data), 2000), [fetchReachEstimation]);

  useEffect(() => {
    if (targets && !_.isEqual(targets, currentTargets)) {
      setIsLoading(true);
      setShowPanel(true);
      setCurrentTargets(targets);
      debouncedUpdateEstimate(targets);
    }
  }, [targets, debouncedUpdateEstimate, currentTargets]);

  return (
    <div className={classNames(classes.reachEstimateControl, 'reach-estimate-control')}>
      {showPanel &&
        <div className={classNames(classes.panel, 'reach-estimate-panel')}>
          <Stack className={classes.panelBody}>
            {isLoading ? (
              <>
                <div className={classes.reachEstimateTitle}>{i18n.getString(title)}</div>
                <Stack verticalAlign="center" styles={{ root: { height: 99 } }} className="reach-estimation-loading" >
                  <Spinner size={SpinnerSize.large} label={i18n.getString(_TL_('Loading'))} ariaLive="assertive" labelPosition="right" />
                </Stack>
              </>
              )
              : (
                <>
                  <Stack horizontal tokens={{ childrenGap: 5 }} className={classes.impressionsCountMessage}>
                    <div>{i18n.getString(`${title}: `)}</div>
                    <div className={classNames(classes.impressionsCount, 'impressions-count')}>{estimatedImpressionsText}</div>
                  </Stack>
                  <Stack tokens={{ childrenGap: 16 }}>
                    <div className={classes.audienceMeterContainer}>
                      <AudienceMeter estimatedImpressions={estimatedImpressions} isImpressionTooSpecific={isImpressionTooSpecific} i18n={i18n} />
                    </div>
                    <div className={classes.estimateNote}>
                      {i18n.getString(_TL_('These are estimates based on your targeting and audience settings.'))}
                    </div>
                    {errorMessage &&
                    <MessageBar
                      messageBarType={MessageBarType.warning}
                      isMultiline
                    >
                      {errorMessage}
                    </MessageBar>
                  }
                  </Stack>
                </>
              )
            }
          </Stack>
        </div>
      }
    </div>
  );
};

ReachEstimate.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    IsAmplifyEnabled: PropTypes.bool,
    IsKPIEstimateForAudienceFeedBasedEnabled: PropTypes.bool,
  }).isRequired,
  shouldServeOnMSAN: PropTypes.bool,
  targets: PropTypes.shape({}),
  accountId: PropTypes.number.isRequired,
  customerId: PropTypes.number.isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    field: PropTypes.string.isRequired,
  }).isRequired,
};

ReachEstimate.defaultProps = {
  targets: null,
  shouldServeOnMSAN: false,
};


const validatedReachEstimate = withCustomValidation(ReachEstimate);

export { validatedReachEstimate as ReachEstimate };
